import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { menusGetAllMine } from "~/api-refs";
import { PageWrapper } from "~/components/page-wrapper";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

export const Route = createFileRoute("/_authenticated/menus/")({
  component: MenusIndexPage,
});

function MenusIndexPage() {
  const {
    hasPermission,
    isLoading: permissionsLoading,
    hasEmployee,
  } = useEmployeePermissions();
  const canEditMenus = hasPermission("editMenus");

  const { data: menusResult, isPending } = useQuery(
    convexQuery(menusGetAllMine, {
      paginationOpts: {
        numItems: 100,
        cursor: null,
      },
    })
  );

  // Loading state
  if (permissionsLoading || isPending) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title="Menus"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title="Menus"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to edit menus
  if (!canEditMenus) {
    return (
      <PageWrapper
        description="Manage menu items, categories, and modifiers"
        title="Menus"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to manage menus. Contact your manager to
            request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  const menus = menusResult?.page;
  const redirectUrl =
    menus?.length && menus[0]
      ? `/menus/${menus[0].id}/categories`
      : "/menus/empty";

  return <Navigate to={redirectUrl} />;
}
